import * as React from "react";
import { Calculator, ChevronDown, Pencil, Plus, Trash2 } from "lucide-react";
import type { AnnualTaxAdjustment, AnnualTaxAdjustmentType, AnnualTaxSettings, AnnualTaxSummary, Expense, IncomeSummary, VehicleExpenseMethod } from "@/types/tax";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Dropdown, type DropdownOption } from "@/components/ui/Dropdown";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { expenseCategoryLabels } from "@/data/atoRules";
import { expenseIncomeTaxDeduction, isVehicleRunningCost, vehicleExpenseBusinessUsePercent } from "@/lib/gst";
import { cn, currency, makeId, parseMoney, percent } from "@/lib/utils";

const methodOptions: DropdownOption<VehicleExpenseMethod>[] = [
  { value: "logbook_actual", label: "Logbook / actual costs", description: "Use vehicle expenses already entered, with each expense's business use." },
  { value: "cents_per_km", label: "Cents per km", description: "Use business kilometres, capped at 5,000 km." },
];

const adjustmentOptions: DropdownOption<AnnualTaxAdjustmentType>[] = [
  { value: "car_depreciation", label: "Car depreciation" },
  { value: "instant_asset_write_off", label: "Instant asset write-off" },
  { value: "other_deduction", label: "Other deduction" },
  { value: "other_income", label: "Other income" },
];

export function AnnualTaxPanel({
  summary,
  onSaveSettings,
  onSaveAdjustment,
  onDeleteAdjustment,
  onOpenIncome,
  onOpenExpense,
}: {
  summary: AnnualTaxSummary;
  onSaveSettings: (settings: AnnualTaxSettings) => Promise<void>;
  onSaveAdjustment: (adjustment: AnnualTaxAdjustment) => void;
  onDeleteAdjustment: (id: string) => void;
  onOpenIncome: (income: IncomeSummary) => void;
  onOpenExpense: (expense: Expense) => void;
}) {
  const [settingsDraft, setSettingsDraft] = React.useState(summary.settings);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [adjustmentType, setAdjustmentType] = React.useState<AnnualTaxAdjustmentType>("car_depreciation");
  const [adjustmentDescription, setAdjustmentDescription] = React.useState("");
  const [adjustmentAmount, setAdjustmentAmount] = React.useState("");
  const [editingAdjustment, setEditingAdjustment] = React.useState<AnnualTaxAdjustment | null>(null);
  const [expandedDeductionId, setExpandedDeductionId] = React.useState<string | null>(null);
  const [openSections, setOpenSections] = React.useState({
    vehicle: false,
    deductions: true,
    adjustments: false,
    warnings: false,
  });
  const savedSettingsKey = [
    summary.settings.id,
    summary.settings.financialYear,
    summary.settings.selectedVehicleMethod,
    summary.settings.businessKilometres,
    summary.settings.centsPerKmRate,
  ].join("|");

  React.useEffect(() => {
    setSettingsDraft(summary.settings);
  }, [savedSettingsKey]);

  const selectedMethod = summary.vehicleMethods.find((method) => method.isSelected);
  const recommendedMethod = summary.vehicleMethods.find((method) => method.isRecommended);
  const isCentsPerKmDraft = settingsDraft.selectedVehicleMethod === "cents_per_km";
  const isLogbookDraft = settingsDraft.selectedVehicleMethod === "logbook_actual";
  const settingsChanged =
    settingsDraft.selectedVehicleMethod !== summary.settings.selectedVehicleMethod ||
    settingsDraft.businessKilometres !== summary.settings.businessKilometres ||
    settingsDraft.centsPerKmRate !== summary.settings.centsPerKmRate;
  const incomeCount = summary.incomeSummaries.length;
  const expenseCount = summary.expenses.length;
  const adjustmentCount = summary.adjustments.length;
  const deductionRows = summary.deductionSummaries
    .filter((item) => item.id !== "car-purchase-review")
    .map((item) => ({ ...item, records: deductionSourceRecords(item.id, summary) }));

  function updateSettings(patch: Partial<AnnualTaxSettings>) {
    if (savingSettings) return;
    setSettingsDraft((current) => ({ ...current, ...patch }));
  }

  async function saveSettings() {
    if (!settingsChanged || savingSettings) return;
    setSavingSettings(true);
    try {
      await onSaveSettings(settingsDraft);
    } finally {
      setSavingSettings(false);
    }
  }

  function addAdjustment(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const amount = parseMoney(adjustmentAmount);
    if (amount <= 0) return;
    onSaveAdjustment({
      id: editingAdjustment?.id || makeId("adj"),
      userId: editingAdjustment?.userId || summary.settings.userId,
      financialYear: editingAdjustment?.financialYear || summary.period.financialYear,
      type: adjustmentType,
      description: adjustmentDescription.trim() || adjustmentOptions.find((option) => option.value === adjustmentType)?.label || "Annual adjustment",
      amount,
    });
    setEditingAdjustment(null);
    setAdjustmentDescription("");
    setAdjustmentAmount("");
  }

  function toggleSection(section: keyof typeof openSections) {
    setOpenSections((current) => ({ ...current, [section]: !current[section] }));
  }

  function editAdjustment(adjustment: AnnualTaxAdjustment) {
    setEditingAdjustment(adjustment);
    setAdjustmentType(adjustment.type);
    setAdjustmentDescription(adjustment.description);
    setAdjustmentAmount(String(adjustment.amount));
    setOpenSections((current) => ({ ...current, adjustments: true }));
  }

  function cancelAdjustmentEdit() {
    setEditingAdjustment(null);
    setAdjustmentDescription("");
    setAdjustmentAmount("");
    setAdjustmentType("car_depreciation");
  }

  return (
    <div className="flex flex-col gap-3 sm:gap-5">
      {incomeCount === 0 && summary.totalDeductions > 0 ? (
        <Alert variant="warning" title="Deductions found without income">
          This financial year has no income records, but it does include expenses or annual adjustments. These figures come from records dated inside this financial year, not from an uploaded income PDF.
        </Alert>
      ) : null}

      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Annual position</CardTitle>
            <CardDescription>{summary.period.startDate} to {summary.period.endDate}</CardDescription>
          </div>
          <Badge variant={summary.netBusinessProfit >= 0 ? "outline" : "credit"}>{summary.netBusinessProfit >= 0 ? "Profit estimate" : "Loss estimate"}</Badge>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-3">
          <Metric title="Net business profit estimate" value={currency(summary.netBusinessProfit)} strong />
          <Metric title="Assessable income" value={currency(summary.assessableIncome)} />
          <Metric title="Total deductions" value={currency(summary.totalDeductions)} tone="credit" />
        </CardContent>
        <div className="border-t border-border px-4 py-3 text-sm leading-6 text-muted sm:px-5">
          Rideshare fares are {currency(summary.rideshareIncome)} GST-inclusive. {currency(summary.incomeGstExcluded)} GST is excluded from annual assessable income.
        </div>
      </Card>

      <Alert title="Income tax worksheet only" className="py-2 text-xs sm:py-3 sm:text-sm">
        Review these figures before lodging in myTax or sending them to a registered tax agent. This app does not lodge your tax return.
      </Alert>

      <Card>
        <CardHeader className="gap-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>Vehicle method comparison</CardTitle>
              <CardDescription>Vehicle expenses already entered in Expenses are reused here. Choose one method to avoid double claiming.</CardDescription>
            </div>
            <button
              type="button"
              className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-muted md:hidden"
              aria-label={openSections.vehicle ? "Collapse vehicle method comparison" : "Expand vehicle method comparison"}
              aria-expanded={openSections.vehicle}
              onClick={() => toggleSection("vehicle")}
            >
              <ChevronDown className={cn("size-5 transition", openSections.vehicle ? "rotate-0" : "-rotate-90")} aria-hidden="true" />
            </button>
          </div>
        </CardHeader>
        <CardContent className={cn("flex flex-col gap-5", !openSections.vehicle && "hidden md:flex")}>
          <FieldGroup className="grid gap-4 md:grid-cols-3">
            <Field>
              <FieldLabel>Selected method</FieldLabel>
              <Dropdown
                label="Selected vehicle method"
                value={settingsDraft.selectedVehicleMethod}
                options={methodOptions}
                disabled={savingSettings}
                onChange={(value) => updateSettings({ selectedVehicleMethod: value })}
              />
            </Field>
            {isCentsPerKmDraft ? (
              <>
                <Field>
                  <FieldLabel htmlFor="business-kilometres">Business kilometres</FieldLabel>
                  <Input
                    id="business-kilometres"
                    inputMode="decimal"
                    value={settingsDraft.businessKilometres}
                    disabled={savingSettings}
                    onChange={(event) => updateSettings({ businessKilometres: parseMoney(event.target.value) })}
                  />
                  <FieldDescription>Cents/km is capped at 5,000 km.</FieldDescription>
                </Field>
                <Field>
                  <FieldLabel htmlFor="cents-rate">Cents per km rate</FieldLabel>
                  <Input
                    id="cents-rate"
                    inputMode="decimal"
                    value={settingsDraft.centsPerKmRate}
                    disabled={savingSettings}
                    onChange={(event) => updateSettings({ centsPerKmRate: parseMoney(event.target.value) })}
                  />
                </Field>
              </>
            ) : null}
            {isLogbookDraft ? (
              <div className="rounded-lg border border-border bg-surface p-3 text-sm leading-6 text-muted md:col-span-2">
                Actual costs use the business-use percentage saved on each vehicle expense. Open running cost details below to review or edit the source records.
              </div>
            ) : null}
          </FieldGroup>

          <div className="grid gap-3 md:grid-cols-2">
            {summary.vehicleMethods.map((method) => (
              <div key={method.method} className="rounded-lg border border-border bg-surface p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-ink">{method.label}</p>
                    <p className="tabular mt-2 text-2xl font-semibold text-ink">{currency(method.deduction)}</p>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    {method.isSelected ? <Badge variant="default">Selected</Badge> : null}
                    {method.isRecommended ? <Badge variant="credit">Higher</Badge> : null}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted">
              Recommended by amount: <span className="font-semibold text-ink">{recommendedMethod?.label || "Review needed"}</span>. Currently selected: <span className="font-semibold text-ink">{selectedMethod?.label || "Review needed"}</span>.
            </p>
            <Button type="button" disabled={!settingsChanged || savingSettings} onClick={saveSettings}>
              <Calculator className="size-4" aria-hidden="true" />
              {savingSettings ? "Saving vehicle settings..." : settingsChanged ? "Save vehicle settings" : "Vehicle settings saved"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="gap-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>Deduction summary</CardTitle>
              <CardDescription>GST credits already claimed are removed from income-tax deductions.</CardDescription>
            </div>
            <button
              type="button"
              className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-muted md:hidden"
              aria-label={openSections.deductions ? "Collapse deduction summary" : "Expand deduction summary"}
              aria-expanded={openSections.deductions}
              onClick={() => toggleSection("deductions")}
            >
              <ChevronDown className={cn("size-5 transition", openSections.deductions ? "rotate-0" : "-rotate-90")} aria-hidden="true" />
            </button>
          </div>
        </CardHeader>
        <CardContent className={cn("flex flex-col gap-4", !openSections.deductions && "hidden md:flex")}>
          <div className="grid gap-3">
            {deductionRows.map((item) => {
              const expanded = expandedDeductionId === item.id;
              const canExpand = item.records.length > 0;

              return (
                <div key={item.id} className="overflow-hidden rounded-lg border border-border bg-white">
                  <button
                    type="button"
                    className="grid min-h-16 w-full gap-3 p-3 text-left hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:grid-cols-[1fr_auto] sm:items-center"
                    aria-expanded={expanded}
                    aria-controls={`annual-deduction-${item.id}`}
                    disabled={!canExpand}
                    onClick={() => setExpandedDeductionId((current) => (current === item.id ? null : item.id))}
                  >
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-ink">{item.label}</span>
                        <Badge variant={item.included ? "credit" : "outline"}>{item.included ? "Included" : "Review"}</Badge>
                      </span>
                      <span className="mt-1 block text-xs leading-5 text-muted">
                        {item.count} records{item.note ? ` | ${item.note}` : ""}
                        {canExpand ? ` | ${expanded ? "Hide records" : "View records"}` : ""}
                      </span>
                    </span>
                    <span className="flex items-center justify-between gap-3 sm:justify-end">
                      <span className="tabular text-lg font-semibold text-ink">{currency(item.amount)}</span>
                      {canExpand ? <ChevronDown className={`size-5 shrink-0 text-muted transition ${expanded ? "rotate-0" : "-rotate-90"}`} aria-hidden="true" /> : null}
                    </span>
                  </button>
                  {expanded ? (
                    <div id={`annual-deduction-${item.id}`} className="border-t border-border bg-surface p-3">
                      <DeductionRecordList
                        records={item.records}
                        onOpenIncome={onOpenIncome}
                        onOpenExpense={onOpenExpense}
                        onEditAdjustment={editAdjustment}
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="gap-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>Annual adjustments</CardTitle>
              <CardDescription>Add tax-agent reviewed depreciation, instant asset write-off, or other annual-only adjustments.</CardDescription>
            </div>
            <button
              type="button"
              className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-muted md:hidden"
              aria-label={openSections.adjustments ? "Collapse annual adjustments" : "Expand annual adjustments"}
              aria-expanded={openSections.adjustments}
              onClick={() => toggleSection("adjustments")}
            >
              <ChevronDown className={cn("size-5 transition", openSections.adjustments ? "rotate-0" : "-rotate-90")} aria-hidden="true" />
            </button>
          </div>
        </CardHeader>
        <CardContent className={cn("flex flex-col gap-5", !openSections.adjustments && "hidden md:flex")}>
          {editingAdjustment ? (
            <Alert title="Editing annual adjustment">
              Update the fields below, then save the adjustment.
            </Alert>
          ) : null}
          <form className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)_180px_auto]" onSubmit={addAdjustment}>
            <Field>
              <FieldLabel>Type</FieldLabel>
              <Dropdown label="Adjustment type" value={adjustmentType} options={adjustmentOptions} onChange={setAdjustmentType} />
            </Field>
            <Field>
              <FieldLabel htmlFor="adjustment-description">Description</FieldLabel>
              <Input id="adjustment-description" value={adjustmentDescription} onChange={(event) => setAdjustmentDescription(event.target.value)} placeholder="Description" />
            </Field>
            <Field>
              <FieldLabel htmlFor="adjustment-amount">Amount</FieldLabel>
              <Input id="adjustment-amount" inputMode="decimal" value={adjustmentAmount} onChange={(event) => setAdjustmentAmount(event.target.value)} placeholder="0.00" />
            </Field>
            <div className="flex items-end">
              <Button type="submit" disabled={parseMoney(adjustmentAmount) <= 0} className="w-full">
                <Plus className="size-4" aria-hidden="true" />
                {editingAdjustment ? "Update" : "Add"}
              </Button>
            </div>
          </form>
          {editingAdjustment ? (
            <div>
              <Button type="button" variant="secondary" size="sm" onClick={cancelAdjustmentEdit}>
                Cancel edit
              </Button>
            </div>
          ) : null}

          {summary.adjustments.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-surface p-4 text-sm text-muted">No annual adjustments yet.</div>
          ) : (
            <div className="grid gap-2">
              {summary.adjustments.map((adjustment) => (
                <div key={adjustment.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-white p-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-ink">{adjustment.description}</p>
                    <p className="mt-1 text-xs text-muted">{adjustmentOptions.find((option) => option.value === adjustment.type)?.label || adjustment.type}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <p className="tabular font-semibold text-ink">{currency(adjustment.amount)}</p>
                    <Button type="button" variant="ghost" size="icon" aria-label={`Edit annual adjustment ${adjustment.description}`} onClick={() => editAdjustment(adjustment)}>
                      <Pencil className="size-4" aria-hidden="true" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" aria-label={`Delete annual adjustment ${adjustment.description}`} onClick={() => onDeleteAdjustment(adjustment.id)}>
                      <Trash2 className="size-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="gap-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle>Review warnings</CardTitle>
              {summary.auditNotes.length > 0 ? <Badge variant="payable">{summary.auditNotes.length}</Badge> : null}
            </div>
            <button
              type="button"
              className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-muted md:hidden"
              aria-label={openSections.warnings ? "Collapse review warnings" : "Expand review warnings"}
              aria-expanded={openSections.warnings}
              onClick={() => toggleSection("warnings")}
            >
              <ChevronDown className={cn("size-5 transition", openSections.warnings ? "rotate-0" : "-rotate-90")} aria-hidden="true" />
            </button>
          </div>
        </CardHeader>
        <CardContent className={cn("grid gap-3", !openSections.warnings && "hidden md:grid")}>
          {summary.auditNotes.map((note) => (
            <Alert key={note.id} variant={note.severity === "danger" ? "danger" : note.severity === "warning" ? "warning" : "info"} title={note.title}>
              {note.message}
            </Alert>
          ))}
          {summary.auditNotes.length === 0 ? <p className="text-sm text-muted">No annual review warnings.</p> : null}
        </CardContent>
      </Card>
    </div>
  );
}

function Metric({ title, value, tone = "default", strong = false }: { title: string; value: string; tone?: "default" | "credit"; strong?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <p className="text-xs font-semibold text-muted">{title}</p>
      <p className={`tabular mt-2 font-semibold ${strong ? "text-3xl" : "text-2xl"} ${tone === "credit" ? "text-credit" : "text-ink"}`}>{value}</p>
    </div>
  );
}

type DeductionRecord = {
  id: string;
  primary: string;
  secondary: string;
  sourceAmount: number;
  includedAmount: number;
  sourceLabel?: string;
  includedLabel?: string;
  sourceIncome?: IncomeSummary;
  sourceExpense?: Expense;
  sourceAdjustment?: AnnualTaxAdjustment;
};

function deductionSourceRecords(id: string, summary: AnnualTaxSummary): DeductionRecord[] {
  if (id === "platform-fees") {
    return summary.incomeSummaries
      .filter((income) => income.platformFeesGross > 0)
      .map((income) => ({
        id: income.id,
        primary: `${income.periodStart} to ${income.periodEnd}`,
        secondary: `${income.provider} platform fees, net of GST`,
        sourceAmount: income.platformFeesGross,
        includedAmount: Math.max(0, income.platformFeesGross - income.platformFeesGst),
        sourceLabel: "Fees paid",
        includedLabel: "Tax deduction",
        sourceIncome: income,
      }));
  }

  if (id === "vehicle-running-costs") {
    return summary.expenses
      .filter((expense) => isVehicleRunningCost(expense.category))
      .map((expense) => {
        const effectiveBusinessUse = vehicleExpenseBusinessUsePercent(expense);
        return {
          id: expense.id,
          primary: `${expense.date} · ${expense.supplier || expenseCategoryLabels[expense.category]}`,
          secondary: `${expenseCategoryLabels[expense.category]} · ${expense.entrySource === "document_scan" ? "Scanned source" : "Manual"} · Business use ${percent(effectiveBusinessUse)}`,
          sourceAmount: expense.amount,
          includedAmount: expenseIncomeTaxDeduction(expense, effectiveBusinessUse),
          sourceLabel: "Expense amount",
          includedLabel: "Tax deduction",
          sourceExpense: expense,
        };
      });
  }

  if (id === "other-expenses") {
    return summary.expenses
      .filter((expense) => !isVehicleRunningCost(expense.category) && expense.category !== "car_purchase")
      .map((expense) => ({
        id: expense.id,
        primary: `${expense.date} · ${expense.supplier || expenseCategoryLabels[expense.category]}`,
        secondary: `${expenseCategoryLabels[expense.category]} · ${expense.entrySource === "document_scan" ? "Scanned source" : "Manual"}`,
        sourceAmount: expense.amount,
        includedAmount: expenseIncomeTaxDeduction(expense),
        sourceLabel: "Expense amount",
        includedLabel: "Tax deduction",
        sourceExpense: expense,
      }));
  }

  if (id === "annual-adjustments") {
    return summary.adjustments
      .filter((adjustment) => adjustment.type !== "other_income")
      .map((adjustment) => ({
        id: adjustment.id,
        primary: adjustment.description,
        secondary: adjustmentOptions.find((option) => option.value === adjustment.type)?.label || adjustment.type.replace(/_/g, " "),
        sourceAmount: adjustment.amount,
        includedAmount: adjustment.amount,
        sourceLabel: "Adjustment",
        includedLabel: "Tax deduction",
        sourceAdjustment: adjustment,
      }));
  }

  if (id === "car-purchase-review") {
    return summary.expenses
      .filter((expense) => expense.category === "car_purchase")
      .map((expense) => ({
        id: expense.id,
        primary: `${expense.date} · ${expense.supplier || "Car purchase"}`,
        secondary: "Car purchase / depreciating asset · needs review",
        sourceAmount: expense.amount,
        includedAmount: 0,
        sourceLabel: "Purchase amount",
        includedLabel: "Included now",
        sourceExpense: expense,
      }));
  }

  return [];
}

function DeductionRecordList({
  records,
  onOpenIncome,
  onOpenExpense,
  onEditAdjustment,
}: {
  records: DeductionRecord[];
  onOpenIncome: (income: IncomeSummary) => void;
  onOpenExpense: (expense: Expense) => void;
  onEditAdjustment: (adjustment: AnnualTaxAdjustment) => void;
}) {
  return (
    <div className="grid gap-2">
      {records.map((record) => (
        <div key={record.id} className="rounded-lg border border-border bg-white p-3">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ink">{record.primary}</p>
              <p className="mt-1 truncate text-xs text-muted">{record.secondary}</p>
            </div>
            <div className="flex items-center justify-between gap-3 sm:justify-end sm:gap-6">
              <div className="grid grid-cols-2 gap-3 text-sm sm:flex sm:gap-6 sm:text-right">
                <div>
                  <p className="text-xs font-semibold text-muted">{record.sourceLabel || "Recorded amount"}</p>
                  <p className="tabular font-semibold text-ink">{currency(record.sourceAmount)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted">{record.includedLabel || "Tax deduction"}</p>
                  <p className="tabular font-semibold text-credit">{currency(record.includedAmount)}</p>
                </div>
              </div>
              {record.sourceIncome || record.sourceExpense || record.sourceAdjustment ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit source ${record.primary}`}
                    title="Edit source"
                    onClick={() => {
                      if (record.sourceIncome) onOpenIncome(record.sourceIncome);
                      if (record.sourceExpense) onOpenExpense(record.sourceExpense);
                      if (record.sourceAdjustment) onEditAdjustment(record.sourceAdjustment);
                    }}
                  >
                    <Pencil className="size-4" aria-hidden="true" />
                  </Button>
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
